import React from 'react';

import { useLocation, useParams } from 'react-router-dom';
import { Button, useIntl } from '@mfw/components';

import {
  useOrganizationContext,
  usePermissionsContext,
  useAuthContext,
  useSnackbar,
} from '../../context';
import { OrgBillingLockedScreen } from '../../allFeatures/Payments/OrgBillingLockedScreen';
import { useOrganizationById } from '../../hooks/organizations';
import { ForbiddenError } from '../../api/http';
import { Spinner } from '../../components';
import { Navigate } from '../components';
import messages from './messages';

export const OrganizationGuard = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { organizationId } = useParams();
  const { pathname } = useLocation();
  const { hasPermission } = usePermissionsContext();
  const { currentUser } = useAuthContext();
  const { currentOrganization, isLoading, organizations, selectOrganization } =
    useOrganizationContext();
  /**
   * Membership is NOT re-derived here. `GET /organizations/:organizationId`
   * already answers it: like every `:organizationId` route it runs the
   * membership precondition in `SecurityGuard.handleOrganizationCheck`, and no
   * route opts out of it (`allowNonMember` is unused across the backend), so a
   * 200 means the caller is an ACTIVE member or a platform account, and a 403
   * means they are not.
   *
   * The client used to answer the same question a second time, from
   * `currentUser.organizationUsers` — a snapshot in `AuthProvider` state that
   * is only replaced by specific triggers (org switch, invitation, token
   * change) and never by merely gaining a membership. When the two disagreed
   * the stale copy won, and members were bounced out of the organization they
   * had just created. One question, one answer, from the side that enforces it.
   */
  const {
    data: organization,
    error: organizationError,
    isPending,
  } = useOrganizationById({ id: organizationId });

  /**
   * Bugfix: a user whose current context is 'personal' (no organization
   * selected — e.g. a platform_admin, or a member whose lastUsedContext is
   * 'personal') opening an org-scoped URL directly never had
   * `currentOrganization` seeded, since the provider's auto-select effect
   * only looks at `lastUsedOrganization`/`lastUsedContext`, never the URL
   * param. That left the `!currentOrganization?.id` check below spinning
   * forever. Once the organization has loaded — which is the server saying the
   * caller may be in it — treat the URL as an explicit selection, the same
   * action the org switcher invokes. The URL remains the
   * authority when another organization is selected: permissions in context
   * belong to that previous organization and must never unlock this one. The
   * ref makes it one-shot per organizationId while the mutation is in flight.
   */
  const autoSelectedIdRef = React.useRef<string | undefined>(undefined);
  const [selectionFailedId, setSelectionFailedId] = React.useState<
    string | undefined
  >(undefined);
  const selectUrlOrganization = React.useCallback(() => {
    if (!organization?.id) return;
    const id = organization.id;
    setSelectionFailedId(undefined);
    autoSelectedIdRef.current = id;
    selectOrganization({ id }).catch(() => {
      autoSelectedIdRef.current = undefined;
      setSelectionFailedId(id);
    });
  }, [organization?.id, selectOrganization]);

  React.useEffect(() => {
    if (!organization?.id) {
      return;
    }
    if (currentOrganization?.id === organization.id) {
      autoSelectedIdRef.current = undefined;
      setSelectionFailedId(undefined);
      return;
    }
    // Reached only for an organization the server let us load, so there is no
    // membership question left to ask before selecting it.
    if (selectionFailedId === organization.id) return;
    if (autoSelectedIdRef.current === organization.id) return;
    selectUrlOrganization();
  }, [
    organization?.id,
    currentOrganization?.id,
    selectUrlOrganization,
    selectionFailedId,
  ]);

  if (isLoading || isPending) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner size="large" />
      </div>
    );
  }
  if (!organizations?.total) {
    setTimeout(() => {
      enqueueSnackbar(formatMessage(messages.noOrganizationsFound), {
        variant: 'error',
      });
    }, 0);
    return <Navigate to="/" />;
  }

  // No organization to enter. WHICH failure it was decides the message, and
  // only a 403 means "not yours": the backend answers the membership
  // precondition with exactly that. Anything else — a 500, no response, an
  // empty body — is the organization failing to load, and reporting that as a
  // membership problem sends the reader hunting for a permission that was
  // never missing.
  if (!organization || !organization.id) {
    const isForbidden = organizationError instanceof ForbiddenError;
    // The failed response carries no name — that IS the failure — but the
    // switcher list often still holds the row (a membership revoked while the
    // tab stayed open is exactly this message's case), and the reader is about
    // to land on the Marketplace, where "this organization" answers nothing.
    // Name it when it is known; never guess it.
    const organizationName = organizations?.data?.find(
      (row) => row?.id === organizationId,
    )?.name;
    setTimeout(() => {
      if (organizationName) {
        enqueueSnackbar(
          formatMessage(
            isForbidden
              ? messages.noOrganizationAccessNamed
              : messages.organizationUnavailableNamed,
            { organizationName },
          ),
          { variant: 'error' },
        );
        return;
      }
      enqueueSnackbar(
        formatMessage(
          isForbidden
            ? messages.noOrganizationAccess
            : messages.organizationUnavailable,
        ),
        { variant: 'error' },
      );
    }, 0);
    return <Navigate to="/" />;
  }

  // `hasPermission` belongs to the organization currently held by the
  // provider. Wait for the URL organization selection above before consulting
  // it, otherwise permissions from org A could open locked org B's Finance.
  if (currentOrganization?.id !== organization.id) {
    if (selectionFailedId === organization.id) {
      return (
        <div
          className="w-full h-full flex flex-col gap-4 items-center justify-center"
          role="alert"
        >
          <span>{formatMessage(messages.organizationSelectionFailed)}</span>
          <Button
            data-testid="organization-selection-retry"
            onClick={selectUrlOrganization}
          >
            {formatMessage(messages.retryOrganizationSelection)}
          </Button>
        </div>
      );
    }
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner size="large" />
      </div>
    );
  }

  // §16 (R-AUD-62): org billing lock — the entire org section is locked until
  // the debt is settled; the finance subtree remains accessible to holders
  // of billing_read (the billing path — BE exempts it the same way via
  // allowWhenBillingLocked)
  const isFinanceRoute = pathname.includes('/finance');
  if (
    organization.billingLocked &&
    !currentUser?.isPlatformUser &&
    !(isFinanceRoute && hasPermission('billing_read'))
  ) {
    // Q3/Task 4: WHY it is locked decides the screen's title, body and CTA —
    // a cancelled, paid-up organization is never labelled a debtor.
    return (
      <OrgBillingLockedScreen
        organizationId={organization.id}
        reason={organization.billingLockReason}
      />
    );
  }

  return children;
};
